import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import Input from '/Foundation/Common/code/Views/Foundation/Common/Input';
import Notification from '/Foundation/Common/code/Views/Foundation/Common/Notifications/Notification';
import { getParameterByName, setCartIdCookie } from '/Foundation/React/code/Utilities/HelperUtilities';
import Spinner from '/Foundation/Common/code/Views/Foundation/Common/Spinner';
import { isValidEmail } from '../MyAccount/utils/helpers.js';
import { fieldIsEmpty } from '/Foundation/Common/code/Helpers/ValidationHelper';
import { br_identify } from '/Foundation/Bloomreach/code/Events/Bloomreach.js';

const MyAccountLogin = ({
  TurnstileSiteKey,
  Translations,
  OverviewPageUrl,
  IsLoggedIn,
  ForgotPasswordUrl,
  CreateAccountUrl,
  Message,
}) => {
  const [email, setEmail] = useState();
  const [password, setPassword] = useState();
  const [message, setMessage] = useState();
  const [turnstileToken, setTurnstileToken] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [touchedFields, setTouchedFields] = useState([]);
  const formFields = ['email', 'password'];
  var turnstileId = '';

  useEffect(() => {
    if (Message?.Content) setMessage(Message);
    if (IsLoggedIn) {
      if (!Message?.Content) redirectToLoggedInDestination();
      return;
    }
    if (window.turnstile?.render) {
      turnstileId = window.turnstile.render('#loginTurnstile', {
        sitekey: TurnstileSiteKey,
        callback: function (token) {
          setTurnstileToken(token);
        },
      });

      return () => {
        window.turnstile.remove(turnstileId);
      };
    }
  }, []);

  const addValidationField = (fieldName) => {
    if (!touchedFields.includes(fieldName)) setTouchedFields(() => [fieldName, ...touchedFields]);
  };

  const redirectToLoggedInDestination = () => {
    const referrer = getParameterByName('referrer');
    if (referrer && !referrer.startsWith('/my-account')) {
      window.location.href = '..' + referrer;
    } else if (OverviewPageUrl) {
      window.location.href = OverviewPageUrl;
    }
  };

  const login = () => {
    setTouchedFields(() => [...formFields, ...touchedFields]);

    if (!password || !email || !turnstileToken || isLoading) return;

    setIsLoading(true);
    setMessage(null);
    const options = {
      method: 'POST',
      body: JSON.stringify({
        UserName: email,
        Password: password,
        turnstileToken: turnstileToken,
      }),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    };
    fetch('/api/sitecore/login/logincustomer', { ...options, credentials: 'same-origin' })
      .then((response) => response.json())
      .then((data) => {
        if (data.Success) {
          setCartIdCookie(data.CartId);
          br_identify(email);
          redirectToLoggedInDestination();
        } else if (data.Messages?.length > 0) {
          setMessage(data.Messages[0]);
        }
        setTurnstileToken('');
        turnstile.reset();
        setIsLoading(false);
      });
  };
  return (
    <>
      {IsLoggedIn && message && (
        <div className="myAccountLogin mt-12">
          <Notification
            Type={message.Type === 0 ? 'success' : 'error'}
            Size={'lg'}
            Message={message.Content}
            ShowIcon={true}
          />
          {OverviewPageUrl && (
            <a
              href={OverviewPageUrl}
              className="mt-7 btn thule-btn-secondary d-flex align-items-center justify-content-center"
            >
              {Translations.MyAccount}
            </a>
          )}
        </div>
      )}
      {!IsLoggedIn && (
        <div className="myAccountLogin mt-12">
          {message && (
            <div className="mb-7">
              <Notification
                Type={message.Type === 0 ? 'success' : 'error'}
                Size={'lg'}
                Message={message.Content}
                ShowIcon={true}
              />
            </div>
          )}
          <h1 className="heading3">{Translations.LogIn}</h1>
          <div className="thuleForm">
            <div className="input-row">
              <Input
                id={`login_email`}
                label={Translations.EmailLabel}
                placeholder={Translations.EmailPlaceholder}
                hasError={touchedFields.includes('email') && (fieldIsEmpty(email) || !isValidEmail(email))}
                errorMessage={Translations.EmailValidationMessage}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                onBlur={() => addValidationField('email')}
                type="email"
                onKeyDown={(e) => {
                  return e.key === ' ' ? e.preventDefault() : null;
                }}
              />
              <Input
                id={`password`}
                type="password"
                label={Translations.PasswordLabel}
                errorMessage={Translations.PasswordValidationMessage}
                hasError={touchedFields.includes('password') && fieldIsEmpty(password)}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                onBlur={() => addValidationField('password')}
              />
              <a
                className="myAccountLogin__forgotPassword paragraph--s mb-7"
                href={ForgotPasswordUrl}
                data-component-label="Reset password"
              >
                {Translations.ForgotPasswordText}
              </a>
              <div id="loginTurnstile" data-theme="light"></div>
              <button onClick={login} className=" mt-6 w-100 btn thule-btn-default">
                {Translations.LogIn}
                {isLoading && <Spinner />}
              </button>
              <div className="horizontalLine pb-8 mb-8 pb-lg-9 mb-lg-9"></div>
            </div>
          </div>
          <div>
            <h2 className="heading3 mb-0">{Translations.CreateAccountTitle}</h2>
            <p className="paragraph text-color--gray80 my-7">{Translations.CreateAccountDescription}</p>
            <a
              className="mb-14 mb-lg-19 btn thule-btn-secondary d-flex align-items-center justify-content-center"
              href={CreateAccountUrl}
              data-component-label="Create account"
            >
              {Translations.CreateAccountButtonText}
            </a>
          </div>
        </div>
      )}
    </>
  );
};

const root = createRoot(document.getElementById('REACT_myAccountLogin'));
root.render(
  <Sentry.ErrorBoundary>
    <MyAccountLogin {..._THULEDATA._myAccountLogin} />
  </Sentry.ErrorBoundary>
);
